import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { 
  ArrowLeft,
  Building,
  Truck,
  Calendar,
  FileText,
  Loader2,
  DollarSign
} from 'lucide-react';
import { challanApiService, ChallanMaster, ChallanDetail, ChallanStatus } from '../services/challanApi';
import { toast } from 'sonner';
import { formatDate, formatDateTime } from '../utils/dateUtils';

export function ChallanViewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [challan, setChallan] = useState<ChallanMaster | null>(null);
  const [challanDetails, setChallanDetails] = useState<ChallanDetail[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      loadChallanData();
    }
  }, [id]);

  const loadChallanData = async () => {
    try {
      setLoading(true);
      const [challanData, detailsData] = await Promise.all([
        challanApiService.getChallanById(id!),
        challanApiService.getChallanDetails(id!)
      ]);
      
      setChallan(challanData);
      // Ensure detailsData is always an array
      const detailsArray = Array.isArray(detailsData) ? detailsData : [];
      // console.log('Challan details loaded:', detailsArray);
      setChallanDetails(detailsArray);
    } catch (error) {
      // console.error('Error loading challan data:', error);
      // Set empty array on error to prevent map errors
      setChallanDetails([]);
      toast.error('Failed to load challan details');
      // Don't navigate away, just show empty state
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/challans');
  };

  const getStatusBadge = (status: ChallanStatus) => {
    const statusConfig = {
      [ChallanStatus.IN_TRANSIT]: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', text: 'In Transit' },
      [ChallanStatus.DELIVERED]: { color: 'bg-green-100 text-green-800 border-green-200', text: 'Delivered' },
      [ChallanStatus.CANCELLED]: { color: 'bg-red-100 text-red-800 border-red-200', text: 'Cancelled' }
    };
    
    const config = statusConfig[status] || statusConfig[ChallanStatus.IN_TRANSIT];
    return <Badge className={config.color}>{config.text}</Badge>;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading challan details...</p>
        </div>
      </div>
    );
  }

  if (!challan) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center py-12">
          <p className="text-gray-600 mb-4">Challan not found</p>
          <Button onClick={handleBack} variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Challans
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBack}
            className="hover:bg-primary-50"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">
              View Challan
            </h1>
            <p className="text-sm text-gray-600 mt-1">View challan details</p>
          </div>
        </div>
      </div>

      {/* Challan Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Receiver Details */}
        <Card className="border-primary-200 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-primary-500 to-primary-600 text-white">
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary-700" />
              Receiver Details
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <div className="space-y-2">
              <Label className="text-primary-700 font-semibold">Name</Label>
              <div className="p-2 bg-gray-50 rounded-md border border-gray-200">
                {challan.toBranch 
                  ? `${challan.toBranch.branchCode} - ${challan.toBranch.branchName}`
                  : challan.toBranchId ? `Branch ID: ${challan.toBranchId}` : 'N/A'}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-primary-700 font-semibold">Address</Label>
              <div className="p-2 bg-gray-50 rounded-md border border-gray-200 min-h-[80px]">
                {challan.toBranch?.address || challan.placeOfSupp || 'N/A'}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-primary-700 font-semibold">Document No</Label>
              <div className="p-2 bg-gray-50 rounded-md border border-gray-200">
                {challan.documentNo || 'N/A'}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Transportation Details */}
        <Card className="border-primary-200 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-primary-500 to-primary-600 text-white">
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary-700" />
              Transportation Details
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <div className="space-y-2">
              <Label className="text-primary-700 font-semibold">Mode of Dispatch</Label>
              <div className="p-2 bg-gray-50 rounded-md border border-gray-200">
                {challan.modeOfDispatch || 'N/A'}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-primary-700 font-semibold">Vehicle No.</Label>
              <div className="p-2 bg-gray-50 rounded-md border border-gray-200">
                {challan.vehNo || 'N/A'}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-primary-700 font-semibold">Date & Time of Supp</Label>
              <div className="p-2 bg-gray-50 rounded-md border border-gray-200">
                {challan.datetimeOfSupply ? formatDateTime(challan.datetimeOfSupply) : 'N/A'}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Item Details */}
      <Card className="border-primary-200 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-primary-500 to-primary-600 text-white">
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary-700" />
            Item Details
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          {!challanDetails || !Array.isArray(challanDetails) || challanDetails.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No items found for this challan.</p>
            </div>
          ) : (
            <div className="overflow-x-auto -mx-6 px-6">
              <div className="min-w-full inline-block align-middle">
                <Table className="min-w-[1400px]">
                  <TableHeader>
                    <TableRow className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                      <TableHead className="min-w-[120px]">Category</TableHead>
                      <TableHead className="min-w-[200px]">Item Name</TableHead>
                      <TableHead className="min-w-[120px]">Heat No.</TableHead>
                      <TableHead className="min-w-[80px]">Qty</TableHead>
                      <TableHead className="min-w-[100px]">Rate</TableHead>
                      <TableHead className="min-w-[80px]">NOP</TableHead>
                      <TableHead className="min-w-[100px]">Hsn</TableHead>
                      <TableHead className="min-w-[80px]">IGST</TableHead>
                      <TableHead className="min-w-[80px]">CGST</TableHead>
                      <TableHead className="min-w-[80px]">SGST</TableHead>
                      <TableHead className="min-w-[100px]">Tax</TableHead>
                      <TableHead className="min-w-[100px]">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Array.isArray(challanDetails) && challanDetails.map((detail) => (
                      <TableRow key={detail.id} className="hover:bg-primary-50/50">
                        <TableCell className="align-top">
                          <span>{detail.itemCategory?.categoryName || 'N/A'}</span>
                        </TableCell>
                        <TableCell className="align-top">
                          <span>{detail.rawItem?.itemName || 'N/A'}</span>
                        </TableCell>
                        <TableCell className="align-top">
                          <span>{detail.lotNo || 'N/A'}</span>
                        </TableCell>
                        <TableCell className="align-top">
                          <span>{detail.qty || 0}</span>
                        </TableCell>
                        <TableCell className="align-top">
                          <span>{formatCurrency(detail.itemRate || 0)}</span>
                        </TableCell>
                        <TableCell className="align-top">
                          <span>0</span>
                        </TableCell>
                        <TableCell className="align-top">
                          <span className="text-sm">{detail.rawItem?.hsn?.hsnCode || '-'}</span>
                        </TableCell>
                        <TableCell className="align-top">
                          <span className="text-sm">
                            {(() => {
                              const hsnData = detail.rawItem?.hsn;
                              if (!hsnData) return '0%';
                              const sameState = challan?.fromBranch?.stateName === challan?.toBranch?.stateName;
                              return sameState ? '0%' : `${hsnData.currentIgstRate || 0}%`;
                            })()}
                          </span>
                        </TableCell>
                        <TableCell className="align-top">
                          <span className="text-sm">
                            {(() => {
                              const hsnData = detail.rawItem?.hsn;
                              if (!hsnData) return '0%';
                              const sameState = challan?.fromBranch?.stateName === challan?.toBranch?.stateName;
                              return sameState ? `${hsnData.currentCgstRate || 0}%` : '0%';
                            })()}
                          </span>
                        </TableCell>
                        <TableCell className="align-top">
                          <span className="text-sm">
                            {(() => {
                              const hsnData = detail.rawItem?.hsn;
                              if (!hsnData) return '0%';
                              const sameState = challan?.fromBranch?.stateName === challan?.toBranch?.stateName;
                              return sameState ? `${hsnData.currentSgstRate || 0}%` : '0%';
                            })()}
                          </span>
                        </TableCell>
                        <TableCell className="align-top">
                          <span className="font-medium">{formatCurrency(detail.amountTax || 0)}</span>
                        </TableCell>
                        <TableCell className="align-top">
                          <span className="font-medium">{formatCurrency(detail.amount || 0)}</span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary */}
      <Card className="border-primary-200 shadow-lg">
        <CardContent className="pt-6">
          <div className="grid grid-cols-3 gap-6 max-w-2xl ml-auto">
            <div className="space-y-2">
              <Label className="text-gray-600">Grand Total Tax</Label>
              <div className="p-3 bg-gray-100 rounded-md font-semibold text-gray-800">
                {formatCurrency(challan.grandTotalTax || 0)}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-600">Total Amount</Label>
              <div className="p-3 bg-gray-100 rounded-md font-semibold text-gray-800">
                {formatCurrency(challan.totalAmount || 0)}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-600">Grand Total</Label>
              <div className="p-3 bg-gradient-to-r from-primary-100 to-primary-200 rounded-md font-bold text-primary-800">
                {formatCurrency(challan.grandTotal || 0)}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Challan Info Footer */}
      <Card className="border-primary-200 shadow-lg">
        <CardContent className="pt-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <Label className="text-gray-500">Challan No</Label>
              <div className="font-semibold text-gray-800">{challan.challanNo}</div>
            </div>
            <div>
              <Label className="text-gray-500">Status</Label>
              <div>{getStatusBadge(challan.status)}</div>
            </div>
            <div>
              <Label className="text-gray-500">From Branch</Label>
              <div className="font-semibold text-gray-800">
                {challan.fromBranch 
                  ? `${challan.fromBranch.branchCode} - ${challan.fromBranch.branchName}`
                  : challan.fromBranchId ? `Branch ID: ${challan.fromBranchId}` : 'N/A'}
              </div>
            </div>
            <div>
              <Label className="text-gray-500">Created On</Label>
              <div className="font-semibold text-gray-800">
                {challan.createdOn ? formatDate(challan.createdOn) : 'N/A'}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

